import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import {
  catchError,
  combineLatest,
  distinctUntilChanged,
  filter,
  finalize,
  first,
  fromEvent,
  map,
  merge,
  Observable,
  of,
  ReplaySubject,
  shareReplay,
  Subject,
  switchMap,
  takeWhile,
  tap,
  throwError,
  timer,
} from 'rxjs';
import { WebSocketSubject } from 'rxjs/webSocket';

import {
  runtimeActions,
  runtimeSelectors,
  WebSocketConfig,
} from '@blaze-runtime/engine-data-access';
import { LogicalQueryNode } from '@blaze-shared/api-contracts-domain';
import { projectContextSelectors } from '@blaze-shared/app-state-data-access';
import { AuthService } from '@blaze-shared/auth-data-access';
import { AlertsApiService, SubscriptionAction } from '@blaze-shared/datasets-data-access';
import {
  isInitialWebsocketInfo,
  isSubscriptionMessage,
  MessagingApiService,
  WebsocketMessage,
} from '@blaze-shared/platform-data-access';
import { isDefined, ObservableBuffer } from '@blaze-shared/shared-util';

import { workerInterval } from './heartbeat/worker-interval';

const PING_INTERVAL_TIME_MS = 30_000;

@Injectable({ providedIn: 'root' })
export class SubscriptionService {
  private actions$ = inject(Actions);
  private alertsApiService = inject(AlertsApiService);
  private authService = inject(AuthService);
  private messagingApiService = inject(MessagingApiService);
  private store = inject(Store);

  private loggedIn$: Subject<void> = new ReplaySubject(1);
  private deletedAlertIds: Set<string> = new Set();

  private accountId$: Observable<string | undefined> = this.store
    .select(projectContextSelectors.selectExternalAccountRowId)
    .pipe(
      map((id) => id || undefined),
      shareReplay(1),
    );
  private projectId$: Observable<string> = this.store.select(runtimeSelectors.selectProjectId);
  private context$: Observable<Record<string, any>> = this.store
    .select(projectContextSelectors.selectVarUser)
    .pipe(
      map((user) => ({ $$User: user })),
      shareReplay(1),
    );

  socketConfig$: Observable<WebSocketConfig> = merge(
    this.loggedIn$,
    fromEvent(window, 'online'),
  ).pipe(
    switchMap(() => {
      const websocket = this.messagingApiService.wssConnect<WebsocketMessage>(
        this.authService.authBearerToken,
      );
      this.pingWhileConnectionAlive(websocket);
      return websocket.asObservable().pipe(
        filter(isInitialWebsocketInfo),
        map(({ channelId }) => ({ websocket, channelId })),
      );
    }),
    shareReplay(1),
  );
  constructor() {
    createEffect(
      () =>
        this.store.select(projectContextSelectors.selectVarUserAndVarAccount).pipe(
          map(({ varUser }) => varUser?.Email),
          filter(isDefined),
          distinctUntilChanged(),
          map(() => undefined),
        ),
      { dispatch: false },
    ).subscribe(this.loggedIn$);

    createEffect(() => this.actions$.pipe(ofType(runtimeActions.alertRemove)), {
      dispatch: false,
    }).subscribe(({ alertId }) => {
      this.deletedAlertIds.add(alertId);
    });
  }

  subscribeFor<T>(
    alertId: string,
    action: SubscriptionAction | SubscriptionAction[],
    { reconnectOnError } = { reconnectOnError: true },
  ): Observable<T> {
    const actions = Array.isArray(action) ? action : [action];
    return combineLatest({
      socketConfig: this.socketConfig$.pipe(first(isDefined)),
      accountId: this.accountId$,
      projectId: this.projectId$,
      context: this.context$,
    }).pipe(
      switchMap(({ socketConfig: { websocket, channelId }, accountId, projectId, context }) => {
        const buffer = ObservableBuffer(100, websocket.asObservable());
        return this.alertsApiService
          .subscribe(alertId, {
            channelId,
            actions,
            externalAccountRowId: accountId,
            projectId,
            inputs: context,
          })
          .pipe(
            catchError((e) => {
              buffer.subscribe().unsubscribe(); // prevent buffer leak
              return throwError(() => e);
            }),
            switchMap(({ subscriptionId }) =>
              buffer.pipe(
                filter(isSubscriptionMessage),
                switchMap(({ routingKey, message }) => {
                  const keys = routingKey.split('.');
                  if (keys[0] === alertId && keys[1] === subscriptionId && keys[2] === action) {
                    return of(message as T);
                  } else {
                    return of();
                  }
                }),
                finalize(() => {
                  if (!this.deletedAlertIds.has(alertId)) {
                    this.alertsApiService.unsubscribe(subscriptionId, alertId).subscribe();
                  }
                }),
              ),
            ),
          );
      }),
      catchError((error) => {
        if (reconnectOnError) {
          // Prevent crashing of the browser when connection keeps failing
          return timer(5000).pipe(
            switchMap(() => this.subscribeFor<T>(alertId, action, { reconnectOnError: true })),
          );
        }
        return throwError(() => error);
      }),
    );
  }

  subscribe<T>(
    tableId: string,
    action: SubscriptionAction,
    query?: LogicalQueryNode,
    { reconnectOnError } = { reconnectOnError: true },
  ): Observable<T> {
    return combineLatest({
      socketConfig: this.socketConfig$.pipe(first(isDefined)),
      accountId: this.accountId$,
      projectId: this.projectId$,
      context: this.context$,
    }).pipe(
      switchMap(({ socketConfig: { websocket, channelId }, accountId, projectId, context }) => {
        const buffer = ObservableBuffer(100, websocket.asObservable());
        return this.alertsApiService
          .subscribeWithoutAlert({
            channelId,
            externalAccountRowId: accountId,
            actions: [action],
            tableId,
            projectId,
            query,
            inputs: context,
          })
          .pipe(
            catchError((e) => {
              buffer.subscribe().unsubscribe(); // prevent buffer leak
              return throwError(() => e);
            }),
            switchMap(({ subscriptionId }) =>
              buffer.pipe(
                filter(isSubscriptionMessage),
                switchMap(({ routingKey, message }) => {
                  const keys = routingKey.split('.');
                  if (!keys[0] && keys[1] === subscriptionId && keys[2] === action) {
                    return of(message as T);
                  }
                  return of();
                }),
                finalize(() => {
                  this.alertsApiService.unsubscribe(subscriptionId).subscribe();
                }),
              ),
            ),
          );
      }),
      catchError((error) => {
        if (reconnectOnError) {
          return timer(5000).pipe(
            switchMap(() => this.subscribe<T>(tableId, action, query, { reconnectOnError: true })),
          );
        }
        return throwError(() => error);
      }),
    );
  }

  private pingWhileConnectionAlive(websocket: WebSocketSubject<any>): void {
    workerInterval(PING_INTERVAL_TIME_MS)
      .pipe(
        tap(() => {
          websocket.next({ message: 'ping' });
        }),
        takeWhile(() => !websocket.closed),
      )
      .subscribe();
  }
}
